import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiSave,
  FiUpload,
  FiImage,
  FiX,
  FiPlus,
} from "react-icons/fi";
import adminApi from "../../api/adminApi";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import Button from "../../components/Button/Button";
import "./CreateCourse.css";

const CreateCourse = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [prerequisites, setPrerequisites] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    level: "",
    status: "active",
    courseSubheading: "",
    features: [],
    batchSize: "",
    lessonsCount: "",
    durationHours: "",
  });

  useEffect(() => {
    // Load available courses for prerequisites
    const loadCourses = async () => {
      try {
        const response = await adminApi.get("/courses");
        setAvailableCourses(
          response.data?.courses || response.data?.data || []
        );
      } catch (err) {
        console.error("Failed to load courses:", err);
      }
    };
    loadCourses();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleAddPricingTier = () => {
    setPricingTiers([
      ...pricingTiers,
      { name: "", price: "", description: "" },
    ]);
  };

  const handleRemovePricingTier = (index) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
  };

  const handlePricingTierChange = (index, field, value) => {
    const updated = [...pricingTiers];
    updated[index][field] = value;
    setPricingTiers(updated);
  };

  const handleAddPrerequisite = (courseId) => {
    if (courseId && !prerequisites.includes(courseId)) {
      setPrerequisites([...prerequisites, courseId]);
    }
  };

  const handleRemovePrerequisite = (courseId) => {
    setPrerequisites(prerequisites.filter((id) => id !== courseId));
  };

  const handleAddFeature = () => {
    const feature = prompt("Enter feature:");
    if (feature) {
      setFormData({ ...formData, features: [...formData.features, feature] });
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const submitData = {
        ...formData,
        price: pricingTiers.length > 0 ? pricingTiers[0].price : formData.price,
        pricingTiers: pricingTiers.length > 0 ? pricingTiers : undefined,
        prerequisites: prerequisites.length > 0 ? prerequisites : undefined,
        lessonsCount: parseInt(formData.lessonsCount) || 0,
        durationHours: parseFloat(formData.durationHours) || 0,
        batchSize: parseInt(formData.batchSize) || 10,
      };

      // Upload image if provided
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("image", imageFile);
        try {
          const imageResponse = await adminApi.post(
            "/upload/image",
            imageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          submitData.imageUrl =
            imageResponse.data.url || imageResponse.data.imageUrl;
        } catch (imgErr) {
          // If upload endpoint doesn't exist, try to set imageUrl directly
          console.warn(
            "Image upload failed, continuing without image:",
            imgErr
          );
        }
      }

      const response = await adminApi.post("/courses", submitData);
      navigate(`/courses/${response.data._id}`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-course-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Course Management</p>
          <h1>Create New Course</h1>
          <p className="subhead">Add a new course to the platform</p>
        </div>
        <Button variant="secondary" onClick={() => navigate("/courses")}>
          <FiArrowLeft size={18} />
          Cancel
        </Button>
      </header>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          <FormInput
            label="Course Title"
            name="title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            placeholder="e.g., Full Stack Web Development"
            required
          />
          <FormInput
            label="Course Subheading"
            name="courseSubheading"
            value={formData.courseSubheading}
            onChange={(e) =>
              setFormData({ ...formData, courseSubheading: e.target.value })
            }
            placeholder="Short tagline for the course"
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={6}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Describe the course content, objectives, and outcomes"
            required
          />

          {/* Image Upload */}
          <div className="image-upload-section">
            <label className="form-input-label">Course Thumbnail/Image</label>
            {imagePreview ? (
              <div className="image-preview-container">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={handleRemoveImage}
                >
                  <FiX size={18} />
                  Remove
                </button>
              </div>
            ) : (
              <div className="image-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="course-image"
                  style={{ display: "none" }}
                />
                <label htmlFor="course-image" className="image-upload-label">
                  <FiImage size={24} />
                  <span>Click to upload image</span>
                  <small>Max 5MB (JPG, PNG, GIF)</small>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>Pricing & Details</h2>

          {/* Base Price */}
          <FormInput
            label="Base Price (₹)"
            name="price"
            type="number"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: e.target.value })
            }
            placeholder="0"
            min="0"
          />

          {/* Pricing Tiers */}
          <div className="pricing-tiers-section">
            <div className="section-header">
              <label className="form-input-label">
                Pricing Tiers (Optional)
              </label>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleAddPricingTier}
              >
                <FiPlus size={16} />
                Add Tier
              </Button>
            </div>
            {pricingTiers.map((tier, index) => (
              <div key={index} className="pricing-tier-item">
                <FormInput
                  label="Tier Name"
                  value={tier.name}
                  onChange={(e) =>
                    handlePricingTierChange(index, "name", e.target.value)
                  }
                  placeholder="e.g., Early Bird, Standard"
                />
                <FormInput
                  label="Price (₹)"
                  type="number"
                  value={tier.price}
                  onChange={(e) =>
                    handlePricingTierChange(index, "price", e.target.value)
                  }
                  placeholder="0"
                />
                <FormInput
                  label="Description"
                  value={tier.description}
                  onChange={(e) =>
                    handlePricingTierChange(
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  placeholder="Tier description"
                />
                <Button
                  type="button"
                  variant="danger"
                  size="small"
                  onClick={() => handleRemovePricingTier(index)}
                >
                  <FiX size={16} />
                  Remove
                </Button>
              </div>
            ))}
          </div>

          <div className="form-grid">
            <FormInput
              label="Duration"
              name="duration"
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: e.target.value })
              }
              placeholder="e.g., 6 months, 12 weeks"
            />
            <FormInput
              label="Duration (Hours)"
              name="durationHours"
              type="number"
              value={formData.durationHours}
              onChange={(e) =>
                setFormData({ ...formData, durationHours: e.target.value })
              }
              placeholder="Total hours"
              min="0"
            />
            <FormInput
              label="Lessons Count"
              name="lessonsCount"
              type="number"
              value={formData.lessonsCount}
              onChange={(e) =>
                setFormData({ ...formData, lessonsCount: e.target.value })
              }
              placeholder="Number of lessons"
              min="0"
            />
            <FormInput
              label="Batch Size"
              name="batchSize"
              type="number"
              value={formData.batchSize}
              onChange={(e) =>
                setFormData({ ...formData, batchSize: e.target.value })
              }
              placeholder="Max students per batch"
              min="1"
            />
          </div>

          <Select
            label="Level"
            name="level"
            value={formData.level}
            onChange={(e) =>
              setFormData({ ...formData, level: e.target.value })
            }
            options={[
              { value: "beginner", label: "Beginner" },
              { value: "intermediate", label: "Intermediate" },
              { value: "advanced", label: "Advanced" },
            ]}
            placeholder="Select level"
          />
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            options={[
              { value: "active", label: "Active" },
              { value: "disabled", label: "Disabled" },
            ]}
          />
        </div>

        {/* Features Section */}
        <div className="form-section">
          <h2>Course Features</h2>
          <div className="features-section">
            <div className="section-header">
              <label className="form-input-label">Features</label>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleAddFeature}
              >
                <FiPlus size={16} />
                Add Feature
              </Button>
            </div>
            <div className="features-list">
              {formData.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span>{feature}</span>
                  <button
                    type="button"
                    className="remove-feature-btn"
                    onClick={() => handleRemoveFeature(index)}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Prerequisites Section */}
        <div className="form-section">
          <h2>Prerequisites</h2>
          <div className="prerequisites-section">
            <Select
              label="Add Prerequisite Course"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddPrerequisite(e.target.value);
                  e.target.value = "";
                }
              }}
              options={[
                { value: "", label: "Select a course..." },
                ...availableCourses
                  .filter((c) => c._id !== formData._id)
                  .map((c) => ({ value: c._id, label: c.title })),
              ]}
            />
            {prerequisites.length > 0 && (
              <div className="prerequisites-list">
                {prerequisites.map((prereqId) => {
                  const course = availableCourses.find(
                    (c) => c._id === prereqId
                  );
                  return course ? (
                    <div key={prereqId} className="prerequisite-item">
                      <span>{course.title}</span>
                      <button
                        type="button"
                        className="remove-prerequisite-btn"
                        onClick={() => handleRemovePrerequisite(prereqId)}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate("/courses")}
          >
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            <FiSave size={18} />
            Create Course
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateCourse;
